import { queryOptions, useQuery } from "@tanstack/react-query";
import { fetchLang } from "../helpers/language.helpers";
import { shouldRetryQuery } from "../helpers/errors.helpers";
import { apiGet } from "../helpers/request.helpers";
import { Course, Exercise, Lesson } from "../types/course.types";

export const courseQueryOptions = (
  publicUuid?: string | null,
  schoolId?: number,
  disableRetry?: boolean
) =>
  queryOptions({
    queryKey: ["course", fetchLang(), publicUuid],
    queryFn: () => fetchCourse(publicUuid, schoolId),
    enabled: !!publicUuid,
    retry: disableRetry ? false : shouldRetryQuery,
    retryOnMount: disableRetry ? false : true,
  });

export function useCourse({
  publicUuid,
  schoolId,
  disableRetry,
}: {
  publicUuid?: string | null;
  schoolId?: number;
  disableRetry?: boolean;
}) {
  return useQuery(courseQueryOptions(publicUuid, schoolId, disableRetry));
}

const fetchCourse = async (publicUuid?: string | null, schoolId?: number) => {
  const extraArgs: Record<string, string | number | null | undefined> = {
    public_uuid: publicUuid,
    language: fetchLang(),
  };
  if (!!schoolId) {
    extraArgs["school_id"] = schoolId;
  }
  const course = await apiGet("/course/show-course", extraArgs);
  let orderInCourse = 0;

  const exerciseMap: Map<string, Exercise> = new Map();

  const updatedCourse: Course & { ExerciseMap: Map<string, Exercise> } = {
    ...course,
    Lessons: course.Lessons.map((l: Lesson) => ({
      ...l,
      Exercises: l.Exercises.map((exercise: Exercise) => {
        orderInCourse++;
        return {
          ...exercise,
          OrderInCourse: orderInCourse,
        };
      }),
    })),
    ExerciseMap: exerciseMap,
  };

  updatedCourse.Lessons.forEach((l: Lesson) => {
    l.Exercises.forEach((e: Exercise) => {
      exerciseMap.set(e.PublicUUID, e);
    });
  });

  return updatedCourse;
};
